// Postal Code Service for Japan
// Uses CDS API for postal code lookup
import axios from 'axios';

import { env } from '@/config/env';

export interface PostalCodeResult {
  prefecture: string;
  city: string;
  town: string;
  fullAddress: string;
}

// API response interface based on the actual API structure
interface ApiResponse {
  data: {
    administrative_area_level_1: string; // Prefecture (都道府県)
    locality: string; // City (市区町村)
    sublocality_level_1: string; // Ward/District (区)
    sublocality_level_2: string; // Town/Neighborhood (町・丁目)
    sublocality_level_3: string | null; // Additional subdivision
  };
  message: string;
}

/**
 * Validates postal code format (3+4 digits)
 */
export const isValidPostalCodeFormat = (postalCode1: string, postalCode2: string): boolean => {
  const code1Pattern = /^\d{3}$/;
  const code2Pattern = /^\d{4}$/;

  return code1Pattern.test(postalCode1) && code2Pattern.test(postalCode2);
};

/**
 * Looks up address by postal code using CDS API
 * @param postalCode1 - First 3 digits of postal code
 * @param postalCode2 - Last 4 digits of postal code
 * @returns PostalCodeResult or null if not found or error
 */
export const lookupPostalCode = async (
  postalCode1: string,
  postalCode2: string
): Promise<PostalCodeResult | null> => {
  // Validate format first
  if (!isValidPostalCodeFormat(postalCode1, postalCode2)) {
    console.warn('Invalid postal code format:', { postalCode1, postalCode2 });
    return null;
  }

  try {
    // Call the CDS API using environment variable
    const apiUrl = `${env.POSTAL_CODE_API_ENDPOINT}/api/admin/address/search`;
    const response = await axios.get<ApiResponse>(apiUrl, {
      params: {
        postal_code_first_half: postalCode1,
        postal_code_latter_half: postalCode2,
      },
      timeout: 10000, // 10 second timeout
    });

    const apiResponse = response.data;

    if (apiResponse.message === '成功' && apiResponse.data) {
      return transformApiResponseToPostalCodeResult(apiResponse.data);
    }
    return null;
  } catch (error) {
    console.error('Error fetching postal code data:', error);
    return null;
  }
};

/**
 * Transforms API response data to PostalCodeResult format
 * @param data - API response data
 * @returns PostalCodeResult
 */
const transformApiResponseToPostalCodeResult = (data: ApiResponse['data']): PostalCodeResult => {
  // Construct the full address from API response parts
  const cityPart = data.locality || '';
  const wardPart = data.sublocality_level_1 || '';
  const townPart = data.sublocality_level_2 || '';

  // Combine city and ward for the city field
  const city = wardPart ? `${cityPart}${wardPart}` : cityPart;

  // Create full address combining all parts
  const addressParts = [data.administrative_area_level_1, cityPart, wardPart, townPart].filter(
    Boolean
  );

  return {
    prefecture: data.administrative_area_level_1,
    city,
    town: townPart,
    fullAddress: addressParts.join(''),
  };
};

/**
 * Formats postal code for display (XXX-XXXX)
 */
export const formatPostalCode = (postalCode1: string, postalCode2: string): string => {
  if (!postalCode1 || !postalCode2) return '';
  return `${postalCode1}-${postalCode2}`;
};

/**
 * Parses formatted postal code (XXX-XXXX) into parts
 */
export const parsePostalCode = (formattedCode: string): { code1: string; code2: string } => {
  const cleaned = formattedCode.replaceAll(/[^\d]/g, '');

  if (cleaned.length === 7) {
    return {
      code1: cleaned.substring(0, 3),
      code2: cleaned.substring(3, 7),
    };
  }

  return { code1: '', code2: '' };
};
